import { QueryClient, UseMutateFunction, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import {
  Organic,
  GETOrganicResponse,
  PATCHOrganicRequest,
  PATCHOrganicResponse,
  POSTOrganicRequest,
  POSTOrganicResponse,
  GETOrganicFunnelsResponse,
  POSTOrganicFunnelRequest,
  POSTOrganicFunnelResponse,
  POSTOrganicFunnelPeriodRequest,
  POSTOrganicFunnelPeriodResponse,
  GETOrganicDirectResidualOrdersResponse,
  POSTOrganicDirectResidualOrdersRequest,
  POSTOrganicDirectResidualOrdersResponse,
  OrganicFunnelPeriod,
  GETOrganicFunnelOrdersAllocationResponse,
  POSTOrganicFunnelOrdersAllocationRequest,
  POSTOrganicFunnelOrdersAllocationResponse,
  OrganicTotalConversionResponse,
  SwitchOrganicFunnelPeriodRequest
} from './types'
import { PeriodTypeQuery, PeriodTypeView } from '@/types'
import { REVENUES_BASE_KEY, TOTALS_KEY, createQueryKey, resetGlobalQueryKey, concatQueryKey } from '@/api/query-keys'

export const ORGANIC_CHANNELS_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`organic-channels`]
})

export const ORGANIC_FUNNELS_KEY = ORGANIC_CHANNELS_KEY.concat([`funnel`])
export const ORGANIC_DIRECT_RESIDUAL_ORDERS_KEY = ORGANIC_FUNNELS_KEY.concat([`direct-residual-orders`])
export const ORGANIC_ORDERS_ALLOCATION_KEY = ORGANIC_FUNNELS_KEY.concat([`orders-allocation`])
export const ORGANIC_TOTAL_RESULTS_KEY = ORGANIC_CHANNELS_KEY.concat([`total-results`])

export const getOrganic = async (financialModelId: string) => {
  const response = await axiosInstance.get<GETOrganicResponse>(`/financial-models/${financialModelId}/organic-channels`)

  return response.data
}

const organicSelect = (data: any) => {
  const isArray = Array.isArray(data)
  const channels = isArray ? data : (data as any).channels || []

  let include_direct_residual_orders = false

  if (!isArray) {
    include_direct_residual_orders = (data as any).include_direct_residual_orders
  } else if (channels.length > 0) {
    include_direct_residual_orders = channels.some((channel: Organic) => channel.include_direct_residual_orders)
  }

  return {
    channels,
    include_direct_residual_orders
  }
}

export const useGetOrganicQuery = (financialModelId: string, enabled?: boolean) =>
  useQuery({
    queryKey: ORGANIC_CHANNELS_KEY.concat([financialModelId]),
    queryFn: () => getOrganic(financialModelId),
    select: organicSelect,
    enabled
  })

export const useGetOrganicSuspenseQuery = (financialModelId: string) =>
  useSuspenseQuery({
    queryKey: ORGANIC_CHANNELS_KEY.concat([financialModelId]),
    queryFn: () => getOrganic(financialModelId),
    select: organicSelect
  })

export const getOrganicById = async (financialModelId: string, id: string) => {
  const response = await axiosInstance.get<Organic>(`/financial-models/${financialModelId}/organic-channels/${id}`)

  return response.data
}

export const postOrganic = async (financialModelId: string, organic: POSTOrganicRequest): Promise<POSTOrganicResponse> => {
  const response = await axiosInstance.post<POSTOrganicResponse>(`/financial-models/${financialModelId}/organic-channels`, organic)

  return response.data
}

export const useCreateOrganicMutation = (
  financialModelId: string,
  funnelMutate: UseMutateFunction<POSTOrganicFunnelResponse, Error, POSTOrganicFunnelRequest, unknown>
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (organic: POSTOrganicRequest) => postOrganic(financialModelId, organic),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ORGANIC_CHANNELS_KEY.concat([financialModelId])
      })
      if (funnelMutate) {
        funnelMutate(
          {
            financial_model_id: data.financial_model_id,
            organic_channel_id: data.id,
            sub_channel_id: null
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: [`organic-funnels`] })
              resetGlobalQueryKey([TOTALS_KEY])
            }
          }
        )
      }
    }
  })
}

const patchOrganicResidualsAndDirect = async (
  financialModelId: string,
  body: {
    include_direct_residual_orders: boolean
  }
) => {
  const response = await axiosInstance.patch<PATCHOrganicResponse>(
    `/financial-models/${financialModelId}/organic-channels/include_direct_residual_orders`,
    body
  )
  return response.data
}

export const usePatchOrganicResidualsAndDirectMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: { include_direct_residual_orders: boolean }) => patchOrganicResidualsAndDirect(financialModelId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ORGANIC_CHANNELS_KEY.concat([financialModelId])
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const patchOrganic = async (financialModelId: string, id: string, organic: PATCHOrganicRequest): Promise<PATCHOrganicResponse> => {
  const response = await axiosInstance.patch<PATCHOrganicResponse>(`/financial-models/${financialModelId}/organic-channels/${id}`, organic)

  return response.data
}

export const useUpdateOrganicMutation = (
  financialModelId: string,
  id: string,
  funnelMutate?: UseMutateFunction<POSTOrganicFunnelResponse, Error, POSTOrganicFunnelRequest, unknown>
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (organic: PATCHOrganicRequest) => patchOrganic(financialModelId, id, organic),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ORGANIC_CHANNELS_KEY.concat([financialModelId])
      })
      if (funnelMutate) {
        data.sub_channels.forEach(subChannel =>
          funnelMutate(
            {
              financial_model_id: data.financial_model_id,
              organic_channel_id: data.id,
              sub_channel_id: subChannel.id
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: ORGANIC_FUNNELS_KEY.concat([financialModelId])
                })
                resetGlobalQueryKey([TOTALS_KEY])
              }
            }
          )
        )
      }
    }
  })
}

export const deleteOrganic = async (financialModelId: string, ids: string[]) => {
  const promises = ids.map(id => axiosInstance.delete(`/financial-models/${financialModelId}/organic-channels/${id}`))

  return Promise.all(promises)
}

export const useDeleteOrganicMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => deleteOrganic(financialModelId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ORGANIC_CHANNELS_KEY.concat([financialModelId])
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getOrganicFunnels = async (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<GETOrganicFunnelsResponse>(`/funnels/${financialModelId}/organic-acquisition-channels`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })

  return response.data
}

export const useGetOrganicFunnelsSuspenseQuery = (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(ORGANIC_FUNNELS_KEY, [financialModelId, period, view ?? `monthly`]),
    queryFn: () => getOrganicFunnels(financialModelId, period, view)
  })

export const postOrganicFunnel = async (organicFunnel: POSTOrganicFunnelRequest) => {
  const response = await axiosInstance.post<POSTOrganicFunnelResponse>(
    `/organic-channels/${organicFunnel.financial_model_id}/funnel?channel_id=${organicFunnel.organic_channel_id}${organicFunnel.sub_channel_id ? `&sub_channel_id=${organicFunnel.sub_channel_id}` : ``}`
  )

  return response.data
}

export const useCreateMultipleOrganicFunnelsMutation = () =>
  useMutation({
    mutationFn: (organicFunnel: POSTOrganicFunnelRequest) => postOrganicFunnel(organicFunnel)
  })

export const postOrganicFunnelPeriod = async (
  financialModelId: string,
  organicChannelId: string,
  subChannelId: string | null,
  funnelId: string | null,
  organicFunnelPeriod: POSTOrganicFunnelPeriodRequest
) => {
  let id = funnelId

  if (!funnelId) {
    const res = await postOrganicFunnel({
      financial_model_id: financialModelId,
      organic_channel_id: organicChannelId,
      sub_channel_id: subChannelId
    })
    id = res.funnel_id
  }

  const response = await axiosInstance.post<POSTOrganicFunnelPeriodResponse>(`/funnels/${id}/organic-acquisition-data`, organicFunnelPeriod)

  return response.data
}

export const postOrganicFunnelPeriodBulk = async (
  financialModelId: string,
  organicChannelId: string,
  subChannelId: string | null,
  funnelId: string | null,
  organicFunnelPeriods: POSTOrganicFunnelPeriodRequest[]
) => {
  let id = funnelId

  if (!funnelId) {
    const res = await postOrganicFunnel({
      financial_model_id: financialModelId,
      organic_channel_id: organicChannelId,
      sub_channel_id: subChannelId
    })
    id = res.funnel_id
  }

  const response = await axiosInstance.post(`/funnels/${id}/organic-acquisition-data/bulk`, {
    periods: organicFunnelPeriods
  })

  return response.data
}

export const getCreateOrganicFunnelPeriodMutationArgs = (queryClient: QueryClient) => ({
  mutationFn: (props: {
    financialModelId: string
    organicChannelId: string
    subChannelId: string | null
    funnelId: string | null
    organicFunnelPeriod: POSTOrganicFunnelPeriodRequest
  }) => postOrganicFunnelPeriod(props.financialModelId, props.organicChannelId, props.subChannelId, props.funnelId, props.organicFunnelPeriod),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ORGANIC_FUNNELS_KEY
    })
    resetGlobalQueryKey([TOTALS_KEY])
  },
  mutationKey: [`organic-funnels`]
})

export const useCreateOrganicFunnelPeriodMutation = () => {
  const queryClient = useQueryClient()

  return useMutation(getCreateOrganicFunnelPeriodMutationArgs(queryClient))
}

export const getOrganicDirectResidualOrders = async (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<GETOrganicDirectResidualOrdersResponse>(`/direct-residual-orders?financial_model_id=${financialModelId}`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })

  return {
    financial_model_id: response.data.financial_model_id,
    customer_segments: response.data.customer_segments.map(customerSegment => ({
      ...customerSegment,
      order_types: customerSegment.order_types.map(orderType => ({
        ...orderType,
        order_data: orderType.order_data.map(order => ({
          ...order,
          order_percentage: order.order_percentage !== null ? Number(order.order_percentage) : null
        }))
      }))
    }))
  }
}

export const useGetOrganicDirectResidualOrdersQuery = (financialModelId: string, period: PeriodTypeQuery, enabled?: boolean, view?: PeriodTypeView) =>
  useQuery({
    queryKey: concatQueryKey(ORGANIC_DIRECT_RESIDUAL_ORDERS_KEY, [financialModelId, period, view ?? `monthly`]),
    queryFn: () => getOrganicDirectResidualOrders(financialModelId, period, view),
    enabled
  })

export const useGetOrganicDirectResidualOrdersSuspenseQuery = (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(ORGANIC_DIRECT_RESIDUAL_ORDERS_KEY, [financialModelId, period, view ?? `monthly`]),
    queryFn: () => getOrganicDirectResidualOrders(financialModelId, period, view)
  })

export const useHistoricalOrganicDirectResidualOrders = ({ financialModelId, view }: { financialModelId?: string; view?: PeriodTypeView }) =>
  useQuery({
    queryKey: concatQueryKey(ORGANIC_DIRECT_RESIDUAL_ORDERS_KEY, [financialModelId ?? ``, `historical`, view ?? `monthly`]),
    queryFn: () => getOrganicDirectResidualOrders(financialModelId!, `historical`, view),
    enabled: Boolean(financialModelId)
  })

export const postOrganicDirectResidualOrders = async (organicDirectResidualOrders: POSTOrganicDirectResidualOrdersRequest) => {
  const response = await axiosInstance.post<POSTOrganicDirectResidualOrdersResponse>(`/direct-residual-orders`, organicDirectResidualOrders)

  return response.data
}

export const postOrganicDirectResidualOrdersBulk = async (organicDirectResidualOrders: POSTOrganicDirectResidualOrdersRequest[]) => {
  const response = await axiosInstance.post<POSTOrganicDirectResidualOrdersResponse>(`/direct-residual-orders/bulk`, {
    periods: organicDirectResidualOrders
  })

  return response.data
}

const switchOrganicFunnelPeriod = async (organicFunnelPeriod: SwitchOrganicFunnelPeriodRequest) => {
  const response = await axiosInstance.post<string>(
    `/funnels/${organicFunnelPeriod.funnel_id}/organic-config/${organicFunnelPeriod.column_name}`,
    {},
    {
      params: {
        input_type: organicFunnelPeriod.input_type,
        year: organicFunnelPeriod.year,
        month: organicFunnelPeriod.month
      }
    }
  )
  return response.data
}

export const useSwitchOrganicFunnelPeriodMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (props: { organicFunnelPeriod: SwitchOrganicFunnelPeriodRequest }) => switchOrganicFunnelPeriod(props.organicFunnelPeriod),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ORGANIC_FUNNELS_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getCreateOrganicDirectResidualOrdersMutationArgs = (queryClient: QueryClient) => ({
  mutationFn: (props: { organicDirectResidualOrders: POSTOrganicDirectResidualOrdersRequest }) =>
    postOrganicDirectResidualOrders(props.organicDirectResidualOrders),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ORGANIC_DIRECT_RESIDUAL_ORDERS_KEY
    })
    resetGlobalQueryKey([TOTALS_KEY])
  }
})
export const useCreateOrganicDirectResidualOrdersMutation = () => {
  const queryClient = useQueryClient()

  return useMutation(getCreateOrganicDirectResidualOrdersMutationArgs(queryClient))
}

const pathMultipleOrganicFunnelPeriods = async (
  organicFunnelPeriods: OrganicFunnelPeriod[],
  financialModelId: string,
  funnelId: string,
  organicChannelId: string,
  subChannelId: string | null
) => {
  const promises = organicFunnelPeriods.map(organicFunnelPeriod => {
    const payload = {
      ...organicFunnelPeriod,
      year: Number(organicFunnelPeriod.period.split(`-`)[0]),
      month: Number(organicFunnelPeriod.period.split(`-`)[1])
    }
    return postOrganicFunnelPeriod(financialModelId, organicChannelId, subChannelId, funnelId, payload)
  })
  const responses = await Promise.allSettled(promises)

  return responses.every(response => response.status === `fulfilled`)
}

export const usePatchMultipleOrganicFunnelPeriodsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (props: {
      organicFunnelPeriods: OrganicFunnelPeriod[]
      funnelId: string
      financialModelId: string
      organicChannelId: string
      subChannelId: string | null
    }) =>
      pathMultipleOrganicFunnelPeriods(
        props.organicFunnelPeriods,
        props.financialModelId,
        props.funnelId,
        props.organicChannelId,
        props.subChannelId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ORGANIC_FUNNELS_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getOrganicOrdersAllocation = async (funnelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<GETOrganicFunnelOrdersAllocationResponse>(`/funnels/${funnelId}/organic-order-allocation`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })

  return {
    funnel_id: response.data.funnel_id,
    customer_segments: response.data.customer_segments.map(customerSegment => ({
      ...customerSegment,
      order_types: customerSegment.order_types.map(orderType => ({
        ...orderType,
        order_allocations: orderType.order_allocations.map(order => ({
          ...order,
          order_percentage: order.order_percentage !== null ? Number(order.order_percentage) : null
        }))
      }))
    }))
  }
}

export const useGetOrganicOrdersAllocationSuspenseQuery = (funnelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(ORGANIC_ORDERS_ALLOCATION_KEY, [funnelId, period, view ?? `monthly`]),
    queryFn: () => getOrganicOrdersAllocation(funnelId, period, view)
  })

export const postOrganicOrdersAllocation = async (funnelId: string, organicOrdersAllocation: POSTOrganicFunnelOrdersAllocationRequest) => {
  const response = await axiosInstance.post<POSTOrganicFunnelOrdersAllocationResponse>(
    `/funnels/${funnelId}/organic-order-allocation`,
    organicOrdersAllocation
  )

  return response.data
}

export const postOrganicOrdersAllocationBulk = async (funnelId: string, organicOrdersAllocation: POSTOrganicFunnelOrdersAllocationRequest[]) => {
  const response = await axiosInstance.post<POSTOrganicFunnelOrdersAllocationResponse>(`/funnels/${funnelId}/organic-order-allocation/bulk`, {
    periods: organicOrdersAllocation
  })

  return response.data
}

export const getCreateOrganicOrdersAllocationMutationArgs = (queryClient: QueryClient) => ({
  mutationFn: (props: { funnelId: string; organicOrdersAllocation: POSTOrganicFunnelOrdersAllocationRequest }) =>
    postOrganicOrdersAllocation(props.funnelId, props.organicOrdersAllocation),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ORGANIC_ORDERS_ALLOCATION_KEY
    })
    resetGlobalQueryKey([TOTALS_KEY])
  }
})

export const useCreateOrganicOrdersAllocationMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(getCreateOrganicOrdersAllocationMutationArgs(queryClient))
}

export const getOrganicTotal = async (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<OrganicTotalConversionResponse>(`/organic-channels/${financialModelId}/total-conversions`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })

  return response.data
}

export const useGetOrganicTotalSuspenseQuery = (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: ORGANIC_TOTAL_RESULTS_KEY,
    queryFn: () => getOrganicTotal(financialModelId, period, view)
  })
